import { yupResolver } from '@hookform/resolvers/yup';
import { BlogPostCustomization, IOperationResultNoValue } from '@ViewModels';
import { css } from 'aphrodite';
import equal from 'fast-deep-equal';
import { FC, useEffect } from 'react';
import { FieldErrors, SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useFindOrCreateAndUpdateBlogStyleMutation } from '../../../../../queries/CustomPageStyle/useFindOrCreateAndUpdateBlogStyleMutation';
import { ColorPicker } from '../../../../../web/components-v2/ColorPicker';
import { bs } from '../../../../../web/styles/styles';
import { useAccountContext } from '../../../../components/blogs';
import { styleSheet } from '../../../../components/blogs/styles';
import { FontSelect } from '../../../../components/FontSelect/FontSelect';
import { Column, Row } from '../../../../components/Layout';
import { DEFAULT_FONT_VALUES, DEFAULT_POST_FORM_VALUES } from './constants';
import { blogPostSchema } from './schemas';
import { TemplateFormWrapper } from './TemplateFormWrapper';

interface PostDesignFormProps {
	customContent?: BlogPostCustomization;
	onUpdateSuccess: () => void;
	onFormDirty: () => void;
}

export const PostDesignForm: FC<PostDesignFormProps> = ({ customContent, onFormDirty, onUpdateSuccess }) => {
	const errorMessages = useErrorMessages();
	const { accountId, impersonationContext } = useAccountContext();

	const {
		setValue,
		getValues,
		handleSubmit,
		formState: { errors, isDirty },
		watch,
	} = useForm<BlogPostCustomization>({
		defaultValues: DEFAULT_POST_FORM_VALUES,
		mode: 'onBlur',
		resolver: yupResolver(blogPostSchema),
	});
	const blogTitleFont = watch('blogTitle.font', DEFAULT_FONT_VALUES);
	const blogHeadlinesFont = watch('blogHeadlines.font', DEFAULT_FONT_VALUES);
	const blogBodyFont = watch('blogBody.font', DEFAULT_FONT_VALUES);
	const buttonFont = watch('showMoreButton.font', DEFAULT_FONT_VALUES);

	// Watch color values for ColorPicker components
	const pageBackgroundColor = watch('blogBody.backgroundColor');
	const buttonBackgroundColor = watch('showMoreButton.backgroundColor');
	const buttonTextColor = watch('showMoreButton.color');
	const buttonHoverColor = watch('showMoreButton.hoverColor');

	const updateBlogPostMutation = useFindOrCreateAndUpdateBlogStyleMutation({
		onSuccess: () => {
			onUpdateSuccess();
			toast.success('Saved changes successfully');
		},
		onError: (error: IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const onValid: SubmitHandler<BlogPostCustomization> = (data, event) => {
		event?.preventDefault();
		event?.stopPropagation();

		updateBlogPostMutation.mutate({
			id: accountId,
			impersonationContext,
			style: data,
		});
	};

	const onInvalid = (validationErrors: FieldErrors<BlogPostCustomization>) => {
		const validationErrorMessages = Object.entries(validationErrors)
			.map(([field, error]) => `${field}: ${error?.message || 'invalid'}`)
			.join(', ');
		toast.error(`Validation failed: ${validationErrorMessages}`);
	};

	// Sync customContent to form values when it changes (like HeaderDesignForm)
	useEffect(() => {
		if (customContent && !equal(customContent, getValues())) {
			Object.entries(customContent).forEach(([key, value]) => {
				if (value !== undefined && value !== null) {
					setValue(key as keyof BlogPostCustomization, value);
				}
			});
		}
	}, [customContent, getValues, setValue]);

	useEffect(() => {
		if (isDirty) {
			onFormDirty();
		}
	}, [isDirty, onFormDirty]);

	return (
		<TemplateFormWrapper
			title='Customize Blog Post'
			isLoading={updateBlogPostMutation.isPending}
			errors={errors}
			onSubmit={handleSubmit(onValid, onInvalid)}
		>
			<Row className={css(styleSheet.gap24)}>
				<Column className={css(bs.flex1, styleSheet.gap24)}>
					<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Background</h2>
					<div className={css(styleSheet.gap8)}>
						<label className={css(styleSheet.label)}>Page Background Color</label>
						<ColorPicker
							value={pageBackgroundColor || '#ffffff'}
							onChange={color => setValue('blogBody.backgroundColor', color, { shouldDirty: true })}
						/>
					</div>
				</Column>
				<Column className={css(bs.flex1, styleSheet.gap24)}>
					<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Title</h2>
					<FontSelect
						initialValue={customContent?.blogTitle?.font}
						onChange={value => setValue('blogTitle.font', value)}
						errors={errors?.blogTitle?.font}
						value={blogTitleFont}
					/>
				</Column>
				<Column className={css(bs.flex1, styleSheet.gap24)}>
					<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Headlines</h2>
					<FontSelect
						initialValue={customContent?.blogHeadlines?.font}
						onChange={value => setValue('blogHeadlines.font', value)}
						errors={errors?.blogHeadlines?.font}
						value={blogHeadlinesFont}
					/>
				</Column>
				<Column className={css(bs.flex1, styleSheet.gap24)}>
					<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Body</h2>
					<FontSelect
						initialValue={customContent?.blogBody?.font}
						onChange={value => setValue('blogBody.font', value)}
						errors={errors?.blogBody?.font}
						value={blogBodyFont}
					/>
				</Column>
				<Column className={css(bs.flex1, styleSheet.gap24)}>
					<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Show More Button</h2>
					<FontSelect
						initialValue={customContent?.showMoreButton?.font}
						onChange={value => setValue('showMoreButton.font', value)}
						errors={errors?.showMoreButton?.font}
						value={buttonFont}
					/>
					<div className={css(styleSheet.gap8)}>
						<label className={css(styleSheet.label)}>Color</label>
						<ColorPicker
							value={buttonBackgroundColor || '#599EED'}
							onChange={color => setValue('showMoreButton.backgroundColor', color, { shouldDirty: true })}
						/>
					</div>
					<div className={css(styleSheet.gap8)}>
						<label className={css(styleSheet.label)}>Text Color</label>
						<ColorPicker
							value={buttonTextColor || '#FFFFFF'}
							onChange={color => setValue('showMoreButton.color', color, { shouldDirty: true })}
						/>
					</div>
					<div className={css(styleSheet.gap8)}>
						<label className={css(styleSheet.label)}>Hover Color</label>
						<ColorPicker
							value={buttonHoverColor || '#035697'}
							onChange={color => setValue('showMoreButton.hoverColor', color, { shouldDirty: true })}
						/>
					</div>
				</Column>
			</Row>
		</TemplateFormWrapper>
	);
};
